import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import useModal from "hooks/modals/useModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import { DeleteScheduledReport, GetScheduledReports, PostScheduledReport, PutScheduledReport } from "api/rpc/2024-04/facilityAdmin/report/scheduledReport";
import { GetFacilityAdmin } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import { GetReport, IReport } from "api/rpc/2024-04/facilityAdmin/report/report";

import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import { valueToString } from "helpers/Helpers";
import { faPencil, faTrash } from "@fortawesome/pro-regular-svg-icons";

import Page from "components/page/Page";
import DataTable from "../../../../components/dataTable/DataTable";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import SelectNew from "components/select/SelectNew";
import MessageDisplay from "components/messageDisplay/MessageDisplay";

import { IUser } from "redux/reducers/models/user";

import "./reports.scss";

export interface IScheduledReport {
    id: number | string;
    facility_id: number | string;
    schedule: string;
    time: string;
    day_of_week: string;
    report: IReport;
    admin_user: IUser;
}

export default function ScheduledReports() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [scheduledReports, setScheduledReports] = useState<IScheduledReport[]>(undefined);
  const [admins, setAdmins] = useState<IUser[]>(undefined);
  const [reports, setReports] = useState<IReport[]>(undefined);

    const {
        state: newScheduledReportModal,
        updateModal: updateNewScheduledReportModal,
        closeModal: closeNewScheduledReportModal,
    } = useModal({
        report_id: null,
        admin_id: null,
        schedule: "daily",
        day_of_week: "All",
    });

    const {
        state: editScheduledReportModal,
        updateModal: updateEditScheduledReportModal,
        closeModal: closeEditScheduledReportModal,
    } = useModal({
        id: null,
        report_id: null,
        admin_id: null,
        schedule: "daily",
        day_of_week: "All",
    });

    const {
        state: deleteScheduledReportPopup,
        updateModal: updateDeleteScheduledReportPopup,
        closeModal: closeDeleteScheduledReportPopup,
    } = useModal({
        id: null,
    });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadScheduledReports(source.token);
    void loadAdmins(source.token);
    void loadReports(source.token);
    return () => source.cancel();
  }, []);

  async function loadScheduledReports(token?: CancelToken) {
    const res = await GetScheduledReports(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading scheduled reports")));
      return;
    }

    setScheduledReports(res.data as IScheduledReport[]);
  }

  async function loadAdmins(token?: CancelToken) {
    const res = await GetFacilityAdmin(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading staff accounts")));
      return;
    }

    setAdmins(res.data as IUser[]);
  }

  async function loadReports(token?: CancelToken) {
    const res = await GetReport(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading reports")));
      return;
    }

    setReports(res.data);
  }

  async function createScheduledReport() {
    const res = await PostScheduledReport({
        report_id: newScheduledReportModal.report_id,
        admin_id: newScheduledReportModal.admin_id,
        schedule: newScheduledReportModal.schedule,
        day_of_week: newScheduledReportModal.day_of_week,
        time: "15:40:00",
    }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.message === "string" ? res.message : t("Error creating scheduled report")));
      return;
    }

    void loadScheduledReports();
    closeNewScheduledReportModal();
  }

  async function updateScheduledReport() {
    const res = await PutScheduledReport({
        id: editScheduledReportModal.id,
        report_id: editScheduledReportModal.report_id,
        admin_id: editScheduledReportModal.admin_id,
        schedule: editScheduledReportModal.schedule,
        day_of_week: editScheduledReportModal.day_of_week,
    }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.message === "string" ? res.message : t("Error updating scheduled report")));
      return;
    }

    void loadScheduledReports();
    closeEditScheduledReportModal();
  }

  async function removeScheduledReport() {
    const res = await DeleteScheduledReport({ id: deleteScheduledReportPopup.id }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.message === "string" ? res.message : t("Error deleting scheduled report")));
      return;
    }

    void loadScheduledReports();
    closeDeleteScheduledReportPopup();
  }

  const primaryAction = {
    content: t("Create Scheduled Report"),
    action: () => updateNewScheduledReportModal({ isOpen: true }),
  };

  return (
    <Page 
        title={t("Scheduled Reports")}
        breadcrumbs={[{ prefix: true, label: t("Reports"), url: "/admin/report" }]}
        primaryAction={primaryAction}
    >
        {scheduledReports === undefined || scheduledReports.length > 0 ? (
            <DataTable
                columns={[
                    { label: t("Report") },
                    { label: t("Schedule") },
                    { label: t("Day") },
                    { label: t("Admin") }, 
                    { label: t("Actions") },
                ]}
                loading={scheduledReports === undefined}
            >
                {scheduledReports?.map((report, index: number) => (
                    <tr key={index}>
                        <td>{report.report?.title}</td>
                        <td>{valueToString(report.schedule)}</td>
                        <td>{valueToString(report.day_of_week)}</td>
                        <td>
                            <div>{report.admin_user?.full_name}</div>
                            <div>{report.admin_user.email}</div>
                        </td>
                        <td className="clickable" >
                            <FontAwesomeIcon className="mr-1" icon={faPencil} onClick={() => updateEditScheduledReportModal({ 
                                isOpen: true,
                                id: report.id,
                                report_id: report.report?.id,
                                admin_id: report.admin_user?.id,
                                schedule: report.schedule,
                                day_of_week: report.day_of_week,
                            })}/>
                            <FontAwesomeIcon icon={faTrash} onClick={() => updateDeleteScheduledReportPopup({ isOpen: true, id: report.id })}/>
                        </td>
                    </tr>
                ))}
            </DataTable>
        ) : (
            <MessageDisplay
                icon="magnifying-glass"
                title={t("No Scheduled Reports")}
                content={t("No scheduled reports were found. Would you like to create one?")}
                actions={[
                    {
                        label: t("Create Scheduled Report"),
                        action: () => updateNewScheduledReportModal({ isOpen: true }),
                    },
                ]}
            />
        )}

        <Portal isMounted={newScheduledReportModal.isOpen}>
            <Sheet
                title={t("Create Scheduled Report")}
                size="small"
                open={newScheduledReportModal.isOpen}
                onCancel={closeNewScheduledReportModal}
                okText={t("Create")}
                onOk={() => void createScheduledReport()}
                overflow
                closable
            >
                <FormLayout>
                    <FormLayout.Group>
                        <SelectNew
                            id={`report_select`}
                            value={newScheduledReportModal?.report_id}
                            onChange={val => updateNewScheduledReportModal({ report_id: val})}
                            options={reports?.map(report => ({ id: report.id, label: report.title}))}
                            label={t("Report")}
                        />
                        <SelectNew
                            id={`admin_select`}
                            value={newScheduledReportModal?.admin_id}
                            onChange={val => updateNewScheduledReportModal({ admin_id: val})}
                            options={admins?.map(admin => ({ id: admin.id, label: admin.full_name }))}
                            label={t("Send to")}
                        />
                    </FormLayout.Group>
                    <FormLayout.Group>
                        <SelectNew
                            id={`schedule_select`}
                            value={newScheduledReportModal?.schedule}
                            onChange={val => updateNewScheduledReportModal({ schedule: val})}
                            options={[
                                { id: "daily", label: t("Daily") },
                                { id: "weekly", label: t("Weekly") },
                            ]}
                            label={t("Schedule")}
                        />
                        <SelectNew
                            id={`day_of_week_select`}
                            value={newScheduledReportModal?.day_of_week}
                            onChange={val => updateNewScheduledReportModal({ day_of_week: val})}
                            options={[
                                { id: "All", label: t("All Days") },
                                { id: "Monday", label: t("Monday") },
                                { id: "Tuesday", label: t("Tuesday") },
                                { id: "Wednesday", label: t("Wednesday") },
                                { id: "Thursday", label: t("Thursday") },
                                { id: "Friday", label: t("Friday") },
                                { id: "Saturday", label: t("Saturday") },
                                { id: "Sunday", label: t("Sunday") },
                            ]}
                            label={t("Day of week")}
                        />
                    </FormLayout.Group>
                </FormLayout>
            </Sheet>
        </Portal>

        <Portal isMounted={editScheduledReportModal.isOpen}>
            <Sheet
                title={t("Edit Scheduled Report")}
                size="small"
                open={editScheduledReportModal.isOpen}
                onCancel={closeEditScheduledReportModal}
                okText={t("Update")}
                onOk={() => void updateScheduledReport()}
                overflow
                closable
            >
                <FormLayout>
                    <FormLayout.Group>
                        <SelectNew
                            id={`report_select`}
                            value={editScheduledReportModal?.report_id}
                            onChange={val => updateEditScheduledReportModal({ report_id: val})}
                            options={reports?.map(report => ({ id: report.id, label: report.title}))}
                            label={t("Report")}
                        />
                        <SelectNew
                            id={`admin_select`}
                            value={editScheduledReportModal?.admin_id}
                            onChange={val => updateEditScheduledReportModal({ admin_id: val})}
                            options={admins?.map(admin => ({ id: admin.id, label: admin.full_name }))}
                            label={t("Send to")}
                        />
                    </FormLayout.Group>
                    <FormLayout.Group>
                        <SelectNew
                            id={`schedule_select`}
                            value={editScheduledReportModal?.schedule}
                            onChange={val => updateEditScheduledReportModal({ schedule: val})}
                            options={[
                                { id: "daily", label: t("Daily") },
                                { id: "weekly", label: t("Weekly") },
                            ]}
                            label={t("Schedule")}
                        />
                        <SelectNew
                            id={`day_of_week_select`}
                            value={editScheduledReportModal?.day_of_week}
                            onChange={val => updateEditScheduledReportModal({ day_of_week: val})}
                            options={[
                                { id: "All", label: t("All Days") },
                                { id: "Monday", label: t("Monday") },
                                { id: "Tuesday", label: t("Tuesday") },
                                { id: "Wednesday", label: t("Wednesday") },
                                { id: "Thursday", label: t("Thursday") },
                                { id: "Friday", label: t("Friday") },
                                { id: "Saturday", label: t("Saturday") },
                                { id: "Sunday", label: t("Sunday") },
                            ]}
                            label={t("Day of week")}
                        />
                    </FormLayout.Group>
                </FormLayout>
            </Sheet>
        </Portal>

        <Portal isMounted={deleteScheduledReportPopup.isOpen} >
            <Popup
                type="warning"
                open={deleteScheduledReportPopup.isOpen}
                title={t("Delete scheduled report")}
                description={t("Are you sure you want to delete this scheduled report?")}
                closable
                onCancel={closeDeleteScheduledReportPopup}
                okText={t("Delete")}
                onOk={() => void removeScheduledReport()}
            />
        </Portal>
    </Page>
  );
}
